<script lang="ts">
	// Chat folders in the sidebar (SAY-1820): a "Folders" section with a "+"
	// that opens the folder dialog; every folder is a collapsible row (name,
	// chat count, "…" menu: New chat in folder · Edit folder · Delete folder)
	// over its chats, pinned first then newest. Folder rows are drop zones for
	// chats dragged from anywhere in the sidebar. A folder's instructions
	// become the system prompt of chats started inside it (home `?folder=`).
	import { goto } from '$app/navigation';
	import FolderIcon from '@lucide/svelte/icons/folder';
	import FolderOpenIcon from '@lucide/svelte/icons/folder-open';
	import FolderPenIcon from '@lucide/svelte/icons/folder-pen';
	import FolderXIcon from '@lucide/svelte/icons/folder-x';
	import SquarePenIcon from '@lucide/svelte/icons/square-pen';
	import EllipsisIcon from '@lucide/svelte/icons/ellipsis';
	import ChevronRightIcon from '@lucide/svelte/icons/chevron-right';
	import NotebookPenIcon from '@lucide/svelte/icons/notebook-pen';
	import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js';
	import SidebarSection from './SidebarSection.svelte';
	import SessionRow from './SessionRow.svelte';
	import FolderDialog from './FolderDialog.svelte';
	import { getSessions } from '$lib/sessions-store.svelte';
	import {
		deleteFolder,
		getFolderMembership,
		getFolders,
		moveToFolder,
		pinRank,
		type ChatFolder,
	} from '$lib/folders-store.svelte';
	import { isTemporary } from '$lib/temp-chats.svelte';
	import { isSelected, isSelectionMode, toggleSelected } from '$lib/chat-selection.svelte';
	import { compareRows, getDraggedChats, isChatDrag } from '$lib/chat-dnd';
	import { toast } from 'svelte-sonner';

	let { activeId }: { activeId?: string } = $props();

	const sessions = getSessions();
	const folders = getFolders();
	const membership = getFolderMembership();

	let sectionOpen = $state(true);
	let dialogOpen = $state(false);
	let editing = $state<ChatFolder | null>(null);
	// Per-folder fold state (open by default; kept for the tab's lifetime).
	const closed = $state<Record<string, boolean>>({});
	// The folder a chat is currently being dragged over.
	let dragOverId = $state<string | null>(null);

	const chatsByFolder = $derived.by(() => {
		const map = new Map<string, typeof sessions>();
		for (const s of sessions) {
			const fid = membership[s.sessionId];
			if (!fid || s.deletedAt || isTemporary(s.sessionId)) continue;
			if (!map.has(fid)) map.set(fid, []);
			map.get(fid)!.push(s);
		}
		for (const rows of map.values()) rows.sort(compareRows(pinRank));
		return map;
	});

	function openDialog(folder: ChatFolder | null): void {
		editing = folder;
		dialogOpen = true;
	}

	async function remove(folder: ChatFolder): Promise<void> {
		try {
			await deleteFolder(folder.id);
			toast.success('Folder deleted', { description: 'Its chats are back in the main list.' });
		} catch (err) {
			toast.error('Could not delete the folder', { description: err instanceof Error ? err.message : String(err) });
		}
	}

	function onDragOver(e: DragEvent, folderId: string): void {
		if (!isChatDrag(e.dataTransfer)) return;
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		dragOverId = folderId;
	}

	async function onDrop(e: DragEvent, folderId: string): Promise<void> {
		dragOverId = null;
		const ids = getDraggedChats(e.dataTransfer).filter((id) => membership[id] !== folderId);
		if (ids.length === 0) return;
		e.preventDefault();
		try {
			await moveToFolder(ids, folderId);
			const f = folders.find((x) => x.id === folderId);
			if (ids.length > 1) toast.success(`Moved ${ids.length} chats to ${f?.name ?? 'folder'}`);
		} catch (err) {
			toast.error('Could not move the chat', { description: err instanceof Error ? err.message : String(err) });
		}
	}
</script>

<SidebarSection name="Folders" id="folders" bind:open={sectionOpen} onAdd={() => openDialog(null)} addLabel="New folder">
	{#if folders.length === 0}
		<p class="px-2 py-0.5 text-[0.6875rem] text-gray-400 dark:text-gray-600">No folders yet</p>
	{:else}
		<div class="pt-1.5" role="list">
			{#each folders as folder (folder.id)}
				{@const rows = chatsByFolder.get(folder.id) ?? []}
				{@const open = !closed[folder.id]}
				<div
					class="group/reveal relative w-full rounded-xl transition {dragOverId === folder.id
						? 'bg-sky-500/10 ring-1 ring-sky-400/60 ring-inset'
						: ''}"
					role="listitem"
					data-testid="folder-item"
					data-folder-id={folder.id}
					ondragover={(e) => onDragOver(e, folder.id)}
					ondragleave={() => {
						if (dragOverId === folder.id) dragOverId = null;
					}}
					ondrop={(e) => void onDrop(e, folder.id)}
				>
					<!-- Folder row: [chevron] [folder icon] [name] … [instructions mark] [count].
					     The trailing cluster hides on hover so the "…" menu never overlaps it. -->
					<div class="flex w-full items-center rounded-xl px-2 py-1.5 whitespace-nowrap transition hover:bg-gray-100 dark:hover:bg-gray-900">
						<button
							type="button"
							class="-my-1.5 flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left"
							aria-expanded={open}
							onclick={() => (closed[folder.id] = open)}
						>
							<span class="flex size-3 shrink-0 items-center justify-center text-gray-400 transition" style="transform: rotate({open ? '90deg' : '0deg'})">
								<ChevronRightIcon class="size-3" strokeWidth={2.5} aria-hidden="true" />
							</span>
							{#if open}
								<FolderOpenIcon class="size-4 shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={1.5} aria-hidden="true" />
							{:else}
								<FolderIcon class="size-4 shrink-0 text-gray-500 dark:text-gray-400" strokeWidth={1.5} aria-hidden="true" />
							{/if}
							<span dir="auto" class="h-5 min-w-0 flex-1 self-center truncate pl-0.5">{folder.name}</span>
						</button>
						<span
							class="flex shrink-0 items-center gap-1.5 self-center pl-2 text-[0.625rem] text-gray-400 group-hover/reveal:invisible group-focus-within/reveal:invisible dark:text-gray-500"
							aria-hidden="true"
						>
							{#if folder.instructions}
								<NotebookPenIcon class="size-3 shrink-0" strokeWidth={1.5} aria-label="Has instructions" />
							{/if}
							<span class="tabular-nums">{rows.length}</span>
						</span>
					</div>
					<div class="hover-reveal absolute top-0 right-1 mr-1.5 flex h-8 items-center">
						<DropdownMenu.Root>
							<DropdownMenu.Trigger
								class="m-0 flex size-5 items-center justify-center self-center transition dark:hover:text-white"
								aria-label="Folder menu"
								title="More"
								data-testid="folder-menu"
							>
								<EllipsisIcon class="size-3.5" strokeWidth={2} aria-hidden="true" />
							</DropdownMenu.Trigger>
							<DropdownMenu.Content align="end" sideOffset={6} class="min-w-[12.5rem]">
								<DropdownMenu.Item onSelect={() => void goto('/?folder=' + encodeURIComponent(folder.id))}>
									<SquarePenIcon strokeWidth={1.5} aria-hidden="true" />
									New chat in folder
								</DropdownMenu.Item>
								<DropdownMenu.Item onSelect={() => openDialog(folder)}>
									<FolderPenIcon strokeWidth={1.5} aria-hidden="true" />
									Edit folder
								</DropdownMenu.Item>
								<DropdownMenu.Separator />
								<DropdownMenu.Item onSelect={() => void remove(folder)}>
									<FolderXIcon strokeWidth={1.5} aria-hidden="true" />
									Delete folder
								</DropdownMenu.Item>
							</DropdownMenu.Content>
						</DropdownMenu.Root>
					</div>
					{#if open && rows.length > 0}
						<div class="ml-[1.4rem] border-l border-gray-200/70 pl-1 dark:border-gray-800" role="list">
							{#each rows as s (s.sessionId)}
								<SessionRow
									row={s}
									isActive={s.sessionId === activeId}
									selectable={isSelectionMode()}
									selected={isSelected(s.sessionId)}
									onToggleSelect={(on) => toggleSelected(s.sessionId, on)}
								/>
							{/each}
						</div>
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</SidebarSection>

<FolderDialog bind:open={dialogOpen} folder={editing} />
